import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { storiesApi, Story } from '../services/api';
import { setFunctional } from '../utils/cookieConsent';

/**
 * Instagram-style stories bar + fullscreen viewer.
 * One instance per dashboard — fetches the global /stories feed and shows
 * rounded thumbnails. Clicking opens a fullscreen viewer that advances
 * automatically through each story (keyed off `duration_seconds`).
 */

interface StoriesBarProps {
  /** Max thumbnails to render inline. Overflow scrolls horizontally. */
  maxVisible?: number;
}

const VIEWED_KEY = 'viewed_story_ids';

function loadViewed(): Set<string> {
  try {
    const raw = localStorage.getItem(VIEWED_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function saveViewed(s: Set<string>) {
  setFunctional(VIEWED_KEY, JSON.stringify(Array.from(s)));
}

const StoriesBar: React.FC<StoriesBarProps> = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [viewed, setViewed] = useState<Set<string>>(() => loadViewed());
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    storiesApi.list()
      .then((res) => setStories(res.data || []))
      .catch(() => setStories([]));
  }, []);

  const current = openIndex !== null ? stories[openIndex] : null;
  // link_url is typed by an admin and rendered as an href for every student/teacher/parent:
  // only http(s) is followed (the server refuses anything else; this also covers stories
  // stored before that check). Same rule as the mobile StoriesBar.
  const safeLink = current?.link_url && /^https?:\/\//i.test(current.link_url) ? current.link_url : null;

  // Auto-advance the open story
  useEffect(() => {
    if (!current) return;
    const durationMs = (current.duration_seconds || 6) * 1000;
    const start = Date.now();
    setProgress(0);
    const tick = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / durationMs) * 100);
      setProgress(pct);
      if (elapsed >= durationMs) {
        // next story or close
        setOpenIndex((i) => {
          if (i === null) return null;
          return i + 1 < stories.length ? i + 1 : null;
        });
      }
    };
    const h = setInterval(tick, 50);
    return () => clearInterval(h);
  }, [current, stories.length]);

  // Mark a story viewed when it opens
  useEffect(() => {
    if (!current) return;
    if (viewed.has(current.id)) return;
    const next = new Set(viewed);
    next.add(current.id);
    setViewed(next);
    saveViewed(next);
  }, [current, viewed]);

  const openAt = useCallback((idx: number) => setOpenIndex(idx), []);
  const close = useCallback(() => setOpenIndex(null), []);
  const next = useCallback(() => setOpenIndex((i) => (i !== null && i + 1 < stories.length ? i + 1 : null)), [stories.length]);
  const prev = useCallback(() => setOpenIndex((i) => (i !== null && i > 0 ? i - 1 : i)), []);

  // Keyboard navigation inside viewer
  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, close, next, prev]);

  if (stories.length === 0) {
    // No stories to show → render nothing (bubbles here are story entries,
    // not content placeholders).
    return null;
  }

  return (
    <>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {stories.map((s, i) => {
          const isViewed = viewed.has(s.id);
          return (
            <button
              key={s.id}
              onClick={() => openAt(i)}
              className="relative w-[72px] h-[72px] xl:w-[92px] xl:h-[92px] rounded-full flex-shrink-0 cursor-pointer focus:outline-none"
              style={{ padding: 3, background: isViewed ? '#d4d4d8' : 'linear-gradient(135deg, #e5599b 0%, #7a07de 50%, #3973bf 100%)' }}
              title={s.caption ?? undefined}
            >
              <div className="w-full h-full rounded-full bg-white p-[2px]">
                {/* Story creatives are tall portraits — crop to cover and center on
                    the top third so the logo/headline stays visible in the circle. */}
                <div
                  className="w-full h-full rounded-full bg-cover"
                  style={{
                    backgroundImage: `url(${s.thumbnail_url || s.media_url})`,
                    backgroundPosition: 'center 30%',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Fullscreen viewer — story creatives are 9:16 portraits, so we cap
          the frame to an Instagram-like card (max ~420px wide, 9:16) centered
          on a dark backdrop instead of filling the whole viewport. */}
      {current && (
        <div
          className="fixed inset-0 z-[80] bg-black/90 flex items-center justify-center p-4"
          onClick={close}
        >
          {/* Close (always visible outside the card) */}
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            className="absolute top-4 right-4 z-20 text-white text-[28px] leading-none w-10 h-10 flex items-center justify-center"
            aria-label="Kapat"
          >×</button>

          <div
            className="relative w-full max-w-[420px] bg-black rounded-[16px] overflow-hidden shadow-2xl"
            style={{ aspectRatio: '9 / 16', maxHeight: 'calc(100vh - 32px)' }}
            onClick={(e) => {
              e.stopPropagation();
              // Tap left third → prev, right third → next, middle → pause/no-op
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              const relX = e.clientX - rect.left;
              if (relX < rect.width / 3) prev();
              else if (relX > (rect.width * 2) / 3) next();
            }}
          >
            {/* Progress bars */}
            <div className="absolute top-0 left-0 right-0 flex gap-1 px-3 pt-3 z-10" onClick={(e) => e.stopPropagation()}>
              {stories.map((_, i) => {
                const fill = i < openIndex! ? 100 : i === openIndex ? progress : 0;
                return (
                  <div key={i} className="flex-1 h-[3px] rounded-full bg-white/30 overflow-hidden">
                    <div className="h-full bg-white" style={{ width: `${fill}%` }} />
                  </div>
                );
              })}
            </div>

            {/* Story media — object-contain so the whole creative is
                visible inside the 9:16 card even when its native ratio
                doesn't match exactly (prevents the top/bottom of the
                image from getting cropped out of view). */}
            <img
              src={current.media_url}
              alt={current.caption ?? ''}
              className="absolute inset-0 w-full h-full object-contain"
              draggable={false}
            />

            {/* Caption + link */}
            {(current.caption || safeLink) && (
              <div
                className="absolute bottom-4 left-0 right-0 px-5 text-center"
                onClick={(e) => e.stopPropagation()}
              >
                {current.caption && (
                  <p className="text-white text-[13px] font-medium drop-shadow-lg mb-3">
                    {current.caption.replace(/^\[(GLOBAL|TEST)\]\s*/i, '')}
                  </p>
                )}
                {safeLink && (
                  <a
                    href={safeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block bg-white text-[#2b2b2b] text-[13px] font-semibold px-5 py-2 rounded-full"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {current.link_label ?? 'Keşfet'}
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default StoriesBar;
